import dayjs from 'dayjs'
import React, { ReactNode } from 'react'
import { Absolute } from '../bases/Absolute'
import { Center } from '../bases/Center'
import { Clickable } from '../bases/Clickable'
import { HStack } from '../bases/HStack'
import { Space } from '../bases/Space'
import { Text } from '../bases/Text'
import { VStack } from '../bases/VStack'
import IconGraphPrev from '../../../../public/images/iconGraphPrev.png'
import IconGraphNext from '../../../../public/images/iconGraphNext.png'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'
import NoData from '@/public/images/noData.png'

export type Props = {
  visibleNodata: boolean
  chartValus: Array<{ unixTimeStamp: number; value: number }>
} & React.ComponentProps<typeof HistoricalPerformanceTargetSwitcher>

export const HistoricalPerformance = (props: Props) => {
  const { t } = useTranslation()
  return (
    <VStack className="min-h-[400px] w-full flex-[3] rounded-xl border border-[#805BE7] bg-black p-5 pb-8">
      <Text className="text-xl font-bold text-white">{t('InstallationStatus.performanceAreaTitle')}</Text>
      {props.visibleNodata ? (
        <Center className="h-full w-full">
          <Image width={140} height={140} src={NoData} alt="No Data" />
        </Center>
      ) : (
        <>
          <VStack className="mt-4">
            <HistoricalPerformanceTargetSwitcher {...props} />
          </VStack>
          <Graph data={props.chartValus} />
        </>
      )}
    </VStack>
  )
}

type HistoricalPerformanceTarget =
  | 'AwardsReceived'
  | 'Earnings'
  | 'Contractors'
  | 'Sparks'
  | 'AvarageOccupancyRateOfAmSlots'
  | 'PlayersInMyGcs'

const HistoricalPerformanceTargetSwitcher = (props: {
  target: HistoricalPerformanceTarget
  onSelected: (target: HistoricalPerformanceTarget) => void
}) => {
  const { t } = useTranslation()
  return (
    <HStack>
      <HistoricalPerformanceTargetSwitcherButton
        active={props.target === 'AwardsReceived'}
        onClick={() => props.onSelected('AwardsReceived')}
      >
        {t('HistoricalPerformance.awardsReceivedTab')}
      </HistoricalPerformanceTargetSwitcherButton>
      <HistoricalPerformanceTargetSwitcherButton
        active={props.target === 'Earnings'}
        onClick={() => props.onSelected('Earnings')}
      >
        {t('HistoricalPerformance.earnings')}
      </HistoricalPerformanceTargetSwitcherButton>
      <HistoricalPerformanceTargetSwitcherButton
        active={props.target === 'Contractors'}
        onClick={() => props.onSelected('Contractors')}
      >
        {t('HistoricalPerformance.contractors')}
      </HistoricalPerformanceTargetSwitcherButton>
      <HistoricalPerformanceTargetSwitcherButton
        active={props.target === 'Sparks'}
        onClick={() => props.onSelected('Sparks')}
      >
        {t('HistoricalPerformance.sparksTab')}
      </HistoricalPerformanceTargetSwitcherButton>
      <HistoricalPerformanceTargetSwitcherButton
        active={props.target === 'AvarageOccupancyRateOfAmSlots'}
        onClick={() => props.onSelected('AvarageOccupancyRateOfAmSlots')}
      >
        {t('HistoricalPerformance.avarageOccupancyRateOfAmSlots')}
      </HistoricalPerformanceTargetSwitcherButton>
      <HistoricalPerformanceTargetSwitcherButton
        active={props.target === 'PlayersInMyGcs'}
        onClick={() => props.onSelected('PlayersInMyGcs')}
      >
        {t('HistoricalPerformance.playersInMyGcs')}
      </HistoricalPerformanceTargetSwitcherButton>
    </HStack>
  )
}

const HistoricalPerformanceTargetSwitcherButton = (props: {
  onClick: () => void
  children: ReactNode
  active: boolean
}) => {
  return (
    <Clickable onClick={props.onClick} className="m-1">
      <Center
        className={`h-8 rounded-full border
        ${props.active ? 'border-akiverse-pink' : 'border-neutral-700'}
        ${props.active ? 'bg-akiverse-pink' : 'bg-transparent'}
        bg-opacity-50 px-4`}
      >
        <Text
          className={`text-xs font-bold
        ${props.active ? 'text-white' : 'text-neutral-700'}`}
        >
          {props.children}
        </Text>
      </Center>
    </Clickable>
  )
}

const Graph = (props: { data: Array<{ unixTimeStamp: number; value: number }> }) => {
  const yAxisCounts = 11
  const sorted = props.data.sort((a, b) => (a.unixTimeStamp > b.unixTimeStamp ? 1 : -1))
  const startUnixTimestamp = sorted[0]?.unixTimeStamp ?? dayjs().unix()
  const endUnixTimestamp = sorted[sorted.length - 1]?.unixTimeStamp
  return (
    <VStack className="relative h-full w-full">
      <Center className="my-7 w-full">
        <Text className="text-lg font-bold text-neon-blue">
          season β1 {dayjs.unix(startUnixTimestamp).format('YYYY.MM.DD')}~
          {endUnixTimestamp ? dayjs.unix(endUnixTimestamp).format('MM.DD') : ''}
        </Text>
      </Center>
      <HStack className="h-full w-full">
        <Center className="mr-8 h-full">
          <Image objectFit="contain" src={IconGraphPrev} alt="Prev" />
        </Center>
        <YLabels values={props.data.map((d) => d.value)} />
        <VStack className="h-full flex-1 py-2">
          <VStack className="h-full w-full">
            <Absolute className="top-0 left-0 bottom-0 right-0 h-full w-full">
              <YAxis count={yAxisCounts} />
            </Absolute>
            <Absolute className="top-0 left-0 bottom-0 right-0 h-full w-full">
              <BarCharts values={props.data} />
            </Absolute>
          </VStack>
          <Absolute className="bottom-[-20px] left-0 right-0 w-full">
            <HStack className="w-full">
              <XLabels unixTimeStamps={props.data.map((d) => d.unixTimeStamp)} />
            </HStack>
          </Absolute>
        </VStack>
        <Center className="ml-8 h-full">
          <Image objectFit="contain" src={IconGraphNext} alt="Next" />
        </Center>
      </HStack>
    </VStack>
  )
}

const YLabels = (props: { values: Array<number> }) => {
  const range = getValuesRange(props.values)
  return (
    <VStack className="h-full items-end pr-2">
      <Text className="text-xs text-neon-blue">{range.max}</Text>
      <Space />
      <Text className="text-xs text-neon-blue">{(range.max - range.min) / 2}</Text>
      <Space />
      <Text className="text-xs text-neon-blue">{range.min}</Text>
    </VStack>
  )
}

const YAxis = (props: { count: number }) => (
  <VStack className="h-full w-full">
    {Array(props.count - 1)
      .fill('')
      .map((_, index) => (
        <React.Fragment key={index}>
          <div className="h-px w-full bg-gray-600" />
          <Space />
        </React.Fragment>
      ))}

    <div className="h-px w-full bg-gray-600" />
  </VStack>
)

const XLabels = (props: { unixTimeStamps: Array<number> }) => (
  <HStack className="w-full">
    <Space />
    {props.unixTimeStamps.map((s) => (
      <>
        <Center className="w-7">
          <Text className="text-xs text-gray-500">{dayjs.unix(s).format('DD')}</Text>
        </Center>
        <Space />
      </>
    ))}
  </HStack>
)

const getValuesRange = (values: Array<number>) => ({
  max: Math.floor(Math.max(...values)),
  min: Math.floor(Math.max(Math.min(...values) - 10, 0)), // 最小値でも棒グラフが表示されるように最小値から10低い数値を最小値として返している
})

const BarCharts = (props: { values: Array<{ value: number }> }) => {
  const range = getValuesRange(props.values.map((v) => v.value))
  const getHeightPercentage = (value: number) => `${(100 * (value - range.min)) / (range.max - range.min)}%`
  return (
    <HStack className="h-full w-full items-end">
      <Space />
      {props.values.map((v, index) => (
        <>
          <Bar key={index} height={getHeightPercentage(v.value)} />
          <Space />
        </>
      ))}
    </HStack>
  )
}

const Bar = (props: { height: string }) => {
  return (
    <div
      className={`flex w-5 rounded-md bg-[#805BE7] transition-all hover:bg-akiverse-pink`}
      style={{ height: props.height }}
    />
  )
}
